import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDollarSign, FiPlus } from 'react-icons/fi';
import toast from 'react-hot-toast';

const DealsPipeline = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      const { data } = await axios.get('/api/crm/deals');
      setDeals(data.deals || []);
    } catch (error) {
      toast.error('Failed to fetch deals');
    } finally {
      setLoading(false);
    }
  };

  const stages = ['Prospecting', 'Qualification', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'];

  const getDealsByStage = (stage) => {
    return deals.filter(deal => deal.stage === stage);
  };

  const getStageValue = (stage) => {
    return getDealsByStage(stage).reduce((sum, deal) => sum + parseFloat(deal.amount || 0), 0);
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/crm')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <FiArrowLeft size={20} />
            Back to CRM Dashboard
          </button>
          <h1 className="text-2xl font-bold">Sales Pipeline</h1>
        </div>
        <button
          onClick={() => navigate('/crm/deals/new')}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
        >
          <FiPlus size={16} />
          New Deal
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
        {stages.map((stage) => (
          <div key={stage} className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-sm">{stage}</h3>
              <span className="text-xs text-gray-500">{getDealsByStage(stage).length}</span>
            </div>
            <div className="text-lg font-bold text-green-600 mb-4">
              ${getStageValue(stage).toLocaleString()}
            </div>
            <div className="space-y-2">
              {getDealsByStage(stage).map((deal) => (
                <div
                  key={deal.id}
                  className="p-3 bg-gray-50 rounded border hover:shadow-sm cursor-pointer"
                  onClick={() => navigate(`/crm/deals/${deal.id}`)}
                >
                  <div className="font-medium text-sm">{deal.dealName}</div>
                  <div className="text-xs text-gray-600 mt-1">{deal.lead?.name}</div>
                  <div className="flex items-center gap-1 mt-2">
                    <FiDollarSign size={12} className="text-green-600" />
                    <span className="text-sm font-medium">{parseFloat(deal.amount || 0).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {deals.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <FiDollarSign size={48} className="mx-auto mb-3 text-gray-300" />
          <p>No deals in pipeline. Create your first deal!</p>
        </div>
      )}
    </div>
  );
};

export default DealsPipeline;